import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Stack,
  Paper,
  Divider,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
} from '@mui/material';
import { GroupAdd, HomeWork, Event, ChevronRight, Edit } from '@mui/icons-material';
import { useAuth } from '../auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import CreateGroupForm from './NewGroup';
import PropertyFormScreen from './NewProperty';
import { useNotification } from '../notification/NotificationProvider';

import ReceivedRequestDetail from './ReceivedRequestDetail';

const ProfileAdminScreen = () => {
  const { dbUser, isLoading, refreshDbUser } = useAuth();
  const { triggerNotification } = useNotification();
  const navigate = useNavigate();

  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedReceivedRequest, setSelectedReceivedRequest] = useState(null);

  if (isLoading) {
    return (
      <Box sx={styles.centeredBox}>
        <CircularProgress sx={{ mr: 2 }} />
        <Typography variant="h5">Cargando datos de usuario...</Typography>
      </Box>
    );
  }

  if (!dbUser) {
    return (
      <Box sx={styles.centeredBox}>
        <Typography variant="h5" color="error">
          Error: No se encontraron datos de usuario. Por favor, inicia sesión de nuevo.
        </Typography>
      </Box>
    );
  }

  const handleGroupClick = () => {
    setSelectedOption('group');
  };

  const handleAcceptInvitation = async (requestId) => {
    try {
      console.log(`Accepting request with ID: ${requestId}`);
      await dbUser.acceptReceivedRequest(requestId);
      setSelectedOption(null);
      setSelectedReceivedRequest(null);
      triggerNotification({
        message: `Invitación aceptada 🔥`,
        sound: true,
        show: true,
        type: 'tiny-top',
        cat: 'received-request-from-group',
      });
      await refreshDbUser(); // Refresh user data after acceptance
    } catch (error) {
      console.error('Error accepting invitation:', error);
      triggerNotification({
        message: 'Error al aceptar la invitación.',
        show: true,
        type: 'tiny-top',
        cat: 'error',
      });
    }
  };

  const handleDenyInvitation = async (requestId) => {
    try {
      console.log(`Denying request with ID: ${requestId}`);
      await dbUser.denyReceivedRequest(requestId);
      setSelectedOption(null);
      setSelectedReceivedRequest(null);
      triggerNotification({
        message: 'Invitación rechazada 😅',
        sound: true,
        show: true,
        type: 'tiny-top',
        cat: 'received-request-from-group',
      });
      await refreshDbUser(); // Refresh user data after denial
    } catch (error) {
      console.error('Error denying invitation:', error);
      triggerNotification({
        message: 'Error al rechazar la invitación.',
        show: true,
        type: 'tiny-top',
        cat: 'error',
      });
    }
  };

  const renderContent = () => {
    switch (selectedOption) {
      case 'group':
        return (
          <CreateGroupForm
            editMode={false}
            editProps={selectedGroup}
            onCancel={() => setSelectedOption(null)}
          />
        );

      case 'editGroup':
        return (
          <CreateGroupForm
            editMode={true}
            editProps={selectedGroup}
            onCancel={() => {
              setSelectedGroup(null);
              setSelectedOption(null);
            }}
          />
        );

      case 'property':
        return <PropertyFormScreen onCancel={() => setSelectedOption(null)} />; // Added onCancel for consistency

      case 'activity':
        return (
          <Typography variant="h6">
            La pantalla de gestión de actividades estará disponible pronto.
            <Button onClick={() => setSelectedOption(null)} sx={{ mt: 2 }} variant="outlined">Volver</Button>
          </Typography>
        );

      case 'receivedRequest':
        return (
          <ReceivedRequestDetail
            request={selectedReceivedRequest}
            onAccept={handleAcceptInvitation}
            onDeny={handleDenyInvitation}
            onBack={() => {
              setSelectedReceivedRequest(null);
              setSelectedOption(null);
            }}
            styles={styles}
          />
        );

      default:
        return (
          <Stack spacing={4} alignItems="center" textAlign="center">
            <Typography variant="h4" fontWeight="bold" sx={{ color: '#1a237e', fontSize: '1.9rem' }}>
              Panel De Control
            </Typography>

            <Typography variant="h6" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
              Crea grupos, añade propiedades o incluso organiza bolos
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ width: '100%' }}>
              <Paper elevation={3} sx={styles.optionCard} onClick={handleGroupClick}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <GroupAdd sx={{ color: '#1e88e5' }} />
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    Crear Grupo
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Personaliza tu identidad dentro de la comunidad.
                </Typography>
              </Paper>

              <Paper elevation={3} sx={styles.optionCard} onClick={() => setSelectedOption('property')}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <HomeWork sx={{ color: '#388e3c' }} />
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#388e3c' }}>
                    Añadir Propiedad
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Registra un nuevo bar, sala o local de eventos.
                </Typography>
              </Paper>

              <Paper elevation={3} sx={styles.optionCard} onClick={() => setSelectedOption('activity')}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <Event sx={{ color: '#d32f2f' }} />
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#d32f2f' }}>
                    Crear Actividad
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Lanza un nuevo bolo, concierto o evento musical.
                </Typography>
              </Paper>
            </Stack>

            <Divider sx={{ width: '80%', my: 4 }} />

            <Box sx={{ width: '100%', maxWidth: 800, textAlign: 'left', px: { xs: 2, sm: 0 } }}> {/* Added horizontal padding for small screens */}
              <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: '#424242' }}>
                Tu Estado Actual:
              </Typography>

              {/* Your Group Requests */}
              <Typography variant="h6" sx={{ color: '#616161', mb: 1 }}>
                Invitaciones Recibidas:
              </Typography>
              {dbUser.misbolos?.receivedRequests?.length > 0 ? (
                <List component={Paper} elevation={1} sx={{ mb: 3, borderRadius: 2 }}>
                  {dbUser.misbolos.receivedRequests.map(rr => (
                    <ListItem
                      key={`rrid-${rr._id}`}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          aria-label="view-details"
                          onClick={() => {
                            setSelectedReceivedRequest(rr);
                            setSelectedOption('receivedRequest');
                          }}
                        >
                          <ChevronRight />
                        </IconButton>
                      }
                      sx={styles.listItemStyled}
                    >
                      <ListItemText
                        primary={<Typography variant="body1" fontWeight="medium">{rr.groupName}</Typography>}
                        secondary={
                          <Typography variant="body2" color="text.secondary" noWrap> {/* noWrap to prevent overflow */}
                            De: {rr.senderName || 'Desconocido'}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" sx={{ ml: 2, mb: 3 }}>
                  No tienes invitaciones recibidas aún.
                </Typography>
              )}

              {/* Groups */}
              <Typography variant="h6" sx={{ color: '#616161', mb: 1 }}>
                Tus Grupos:
              </Typography>
              {dbUser.misbolos?.groups?.length > 0 ? (
                <List component={Paper} elevation={1} sx={{ mb: 3, borderRadius: 2 }}>
                  {dbUser.misbolos.groups.map(group => (
                    <ListItem
                      key={`groupId-${group._id}`}
                      secondaryAction={
                        dbUser._id === group.owner && ( // Only show edit if user is owner
                          <IconButton
                            edge="end"
                            aria-label="edit-group"
                            onClick={() => {
                              setSelectedGroup(group);
                              setSelectedOption('editGroup');
                            }}
                          >
                            <Edit />
                          </IconButton>
                        )
                      }
                      sx={styles.listItemStyled}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body1" fontWeight="medium">
                            {group.name}
                          </Typography>
                        }
                        secondary={
                          <Stack direction="column" spacing={1} mt={0.5}>
                            <Typography variant="body2" color="text.secondary">
                              Miembros: {group.members.length}
                            </Typography>

                            {group.pendingMembers.length > 0 && (
                              <Chip
                                label={`Pendientes: ${group.pendingMembers.length}`}
                                size="small"
                                sx={{
                                  height: 20,
                                  alignSelf: 'start',
                                  backgroundColor: '#767573',
                                  color: '#fff',           // Make sure the text is visible over the background
                                }}
                              />
                            )}

                            <Chip
                              label={dbUser._id === group.owner ? 'Administrador' : 'Miembro'}
                              size="small"
                              sx={{
                                height: 20,
                                alignSelf: 'start',
                                backgroundColor: dbUser._id === group.owner ? '#d03248' : '#32d099',
                                color: '#fff',  // White text for contrast
                              }}
                            />
                          </Stack>
                        }
                      />

                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" sx={{ ml: 2, mb: 3 }}>
                  No tienes ningún grupo aún.
                </Typography>
              )}

              {/* Properties */}
              <Typography variant="h6" sx={{ mt: 3, color: '#616161', mb: 1 }}>
                Tus Propiedades:
              </Typography>
              {dbUser.misbolos?.properties && Object.keys(dbUser.misbolos.properties).length > 0 ? (
                <List component={Paper} elevation={1} sx={{ mb: 3, borderRadius: 2 }}>
                  {Object.values(dbUser.misbolos.properties).map(property => (
                    <ListItem key={property.name} sx={styles.listItemStyled}>
                      <ListItemText primary={<Typography variant="body1" fontWeight="medium" noWrap>{property.name}</Typography>} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" sx={{ ml: 2, mb: 3 }}>
                  No has añadido ninguna propiedad aún.
                </Typography>
              )}

              {/* Activities */}
              <Typography variant="h6" sx={{ mt: 3, color: '#616161', mb: 1 }}>
                Tus Bolos Pendientes:
              </Typography>
              {dbUser.misbolos?.activities?.length > 0 ? (
                <List component={Paper} elevation={1} sx={{ mb: 3, borderRadius: 2 }}>
                  {dbUser.misbolos.activities.map(activity => (
                    <ListItem key={`activityIdOld-${activity._id}-${activity.title}`} sx={styles.listItemStyled}>
                      <ListItemText primary={<Typography variant="body1" fontWeight="medium" noWrap>{activity.title}</Typography>} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" sx={{ ml: 2, mb: 3 }}>
                  No estás relacionado con ninguna actividad aún.
                </Typography>
              )}
            </Box>
          </Stack>
        );
    }
  };

  return (
    <Box sx={styles.screenContainer}>
      <Container maxWidth="md">{renderContent()}</Container>
    </Box>
  );
};

const styles = {
  screenContainer: {
    minHeight: '100vh',
    //background: 'linear-gradient(to bottom right, #f0f4f8, #cce0ff)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 2, // Reduced overall padding for small screens
  },
  centeredBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
  },
  optionCard: {
    flex: 1,
    minWidth: 200, // Slightly reduced minWidth for better fit
    padding: 3,
    borderRadius: 3,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    backgroundColor: '#f9f9f9',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
    },
  },
  listItemStyled: {
    borderBottom: '1px solid #e0e0e0',
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  detailCard: {
    padding: 4,
    borderRadius: 2,
    backgroundColor: '#ffffff',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    textAlign: 'center',
    maxWidth: 500,
    margin: 'auto',
  },
};

export default ProfileAdminScreen;